/**
 * ContributorList Component
 * Displays list of contributors with filtering and selection
 */

import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  CircularProgress,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  CheckCircle as ActiveIcon,
  HourglassEmpty as PendingIcon,
  Block as DisabledIcon
} from '@mui/icons-material';
import { useContributors, useContributorStats } from '../../hooks/useContributors';

const STATUS_CONFIG = {
  activated: { label: 'Activated', color: 'success', icon: <ActiveIcon fontSize="small" /> },
  pending: { label: 'Pending', color: 'warning', icon: <PendingIcon fontSize="small" /> },
  disabled: { label: 'Disabled', color: 'error', icon: <DisabledIcon fontSize="small" /> },
  deleted: { label: 'Deleted', color: 'default', icon: null }
};

export const ContributorList = ({
  selectedId,
  onSelect,
  statusFilter,
  onStatusFilterChange,
  pendingApprovalFilter,
  onPendingApprovalFilterChange,
  searchTerm,
  onSearchChange
}) => {
  // Fetch contributors
  const { data, isLoading, error } = useContributors({
    status: statusFilter || undefined,
    pending_approval: pendingApprovalFilter ? 'true' : undefined,
    search: searchTerm || undefined,
    limit: 100
  });

  // Fetch stats for badge counts
  const { data: stats } = useContributorStats();

  const contributors = data?.contributors || [];

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Filters */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder="Search handle or email..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
        />

        <FormControl size="small" fullWidth sx={{ mb: 1 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              onStatusFilterChange(e.target.value);
              if (e.target.value) {
                onPendingApprovalFilterChange(false);
              }
            }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="activated">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Activated
                {stats?.activated > 0 && (
                  <Chip label={stats.activated} size="small" color="success" />
                )}
              </Box>
            </MenuItem>
            <MenuItem value="pending">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Pending
                {stats?.pending > 0 && (
                  <Chip label={stats.pending} size="small" color="warning" />
                )}
              </Box>
            </MenuItem>
            <MenuItem value="disabled">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Disabled
                {stats?.disabled > 0 && (
                  <Chip label={stats.disabled} size="small" color="error" />
                )}
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        {/* Quick filter for pending approval */}
        <Chip
          label={`Pending Approval${stats?.pending_approval ? ` (${stats.pending_approval})` : ''}`}
          color={pendingApprovalFilter ? 'primary' : 'default'}
          variant={pendingApprovalFilter ? 'filled' : 'outlined'}
          onClick={() => {
            onPendingApprovalFilterChange(!pendingApprovalFilter);
            if (!pendingApprovalFilter) {
              onStatusFilterChange('');
            }
          }}
          sx={{ mt: 1 }}
        />
      </Box>

      {/* Stats Summary */}
      {stats && (
        <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary">
            Total: {stats.total} | Active: {stats.activated} | Pending Approval: {stats.pending_approval}
          </Typography>
        </Box>
      )}

      <Divider />

      {/* Contributors List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Typography color="error">
              Error loading contributors: {error.message}
            </Typography>
          </Box>
        ) : contributors.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography color="text.secondary" align="center">
              No contributors found
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {contributors.map((contributor) => {
              const statusConfig = STATUS_CONFIG[contributor.status] || STATUS_CONFIG.pending;
              const needsApproval = contributor.wantsPublicListing && !contributor.isPubliclyVisible;

              return (
                <ListItem key={contributor.id} disablePadding divider>
                  <ListItemButton
                    selected={selectedId === contributor.id}
                    onClick={() => onSelect(contributor)}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" sx={{ flex: 1 }}>
                          {contributor.handle}
                        </Typography>
                        <Chip
                          label={statusConfig.label}
                          size="small"
                          color={statusConfig.color}
                          variant="outlined"
                        />
                      </Box>

                      <Typography variant="caption" color="text.secondary" display="block">
                        {contributor.contact_email}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Joined: {formatDate(contributor.createdAt)}
                        </Typography>

                        {contributor.submission_count > 0 && (
                          <Chip
                            label={`${contributor.submission_count} submissions`}
                            size="small"
                            variant="outlined"
                          />
                        )}

                        {contributor.published_games > 0 && (
                          <Chip
                            label={`${contributor.published_games} published`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}

                        {needsApproval && (
                          <Chip
                            label="Needs Approval"
                            size="small"
                            color="warning"
                          />
                        )}
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* Footer with count */}
      {data && (
        <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {contributors.length} of {data.totalCount} contributors
          </Typography>
        </Box>
      )}
    </Box>
  );
};
